<script setup lang="ts">
/**
 * Right cell for the mobile Finances table. Stacks the desktop table's
 * Balance/Revenue/per-variant columns into one cell (mirrors
 * `ClientMobileRightCell`'s role): a Balance/Revenue row on top, then a
 * list of this period's visited lesson types with their counts — or an
 * italic "No lessons this period" placeholder when `lessons` is empty.
 *
 * `balance`/`revenue` are minor-unit amounts (same convention as
 * `ClientMobileRightCell`/`MoneyCell`); `lessons` is pre-filtered by the
 * caller to non-zero counts only (see `FinancesTab`'s `mobileColumns`).
 */
import { computed } from 'vue'
import { formatMoney } from '@/utils/formatMoney'

interface Lesson {
  name: string
  price: number | null
  count: number
}

const props = withDefaults(
  defineProps<{
    balance?: number | null
    revenue?: number
    lessons?: Lesson[]
  }>(),
  { lessons: () => [] },
)

const hasBalance = computed(
  () => props.balance !== undefined && props.balance !== null && !Number.isNaN(props.balance),
)

const balanceResult = computed(() =>
  hasBalance.value ? formatMoney({ amount: props.balance!, includeCurrency: true }) : null,
)

const balanceStyle = computed(() => {
  if (!hasBalance.value) return {}
  const raw = props.balance!
  if (raw < 0) return { color: 'var(--color-money-negative)' }
  if (raw > 0) return { color: 'var(--color-money-positive)' }
  return { color: 'var(--color-tufte-muted)' }
})

const revenueResult = computed(() =>
  formatMoney({ amount: props.revenue ?? 0, includeCurrency: true }),
)
</script>

<template>
  <td class="finance-mobile-right font-tufte text-tufte-text">
    <div class="right-layout">
      <div class="bal-rev">
        <div class="bal-rev__item">
          <span class="bal-rev__label">Balance</span>
          <span v-if="balanceResult" class="bal-rev__value" :style="balanceStyle">
            {{ balanceResult.formatted }}
          </span>
          <span v-else class="bal-rev__value text-tufte-muted">–</span>
        </div>
        <div class="bal-rev__item bal-rev__item--right">
          <span class="bal-rev__label">Revenue</span>
          <span class="bal-rev__value">{{ revenueResult.formatted }}</span>
        </div>
      </div>
      <div v-if="lessons.length > 0" class="lessons-list">
        <div v-for="lesson in lessons" :key="lesson.name + lesson.price" class="lessons-list__row">
          <span class="lessons-list__name text-tufte-muted">{{ lesson.name }}</span>
          <span class="lessons-list__count">{{ lesson.count }}</span>
        </div>
      </div>
      <p v-else class="lessons-empty text-tufte-muted">No lessons this period</p>
    </div>
  </td>
</template>

<style scoped>
.finance-mobile-right {
  vertical-align: top;
}

.right-layout {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.bal-rev {
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.bal-rev__item {
  display: flex;
  flex-direction: column;
}

.bal-rev__item--right {
  align-items: flex-end;
}

.bal-rev__label {
  font-family: var(--font-tufte-sans);
  font-size: 0.62rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  white-space: nowrap;
}

/*
 * `white-space: nowrap` is required here: without it, a negative amount
 * (e.g. "−$18") can line-wrap between the minus sign and the digits on
 * narrow phones, which reads as a stray duplicate number under the label.
 */
.bal-rev__value {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.lessons-list {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  border-top: 1px solid color-mix(in srgb, var(--color-tufte-border) 60%, transparent);
  padding-top: 0.3rem;
}

.lessons-list__row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
}

.lessons-list__count {
  font-family: var(--font-tufte-mono);
  font-weight: 600;
}

.lessons-empty {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  font-style: italic;
}
</style>
